import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { EmptyRow, SkeletonRows } from "@/components/TableStates";
import { Button } from "@/components/ui/button";
import { RuleDialog } from "@/features/expenses/RuleDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { del, get } from "@/lib/api";
import { allClientsQuery } from "@/lib/clients";
import { date } from "@/lib/format";
import { ruleSharesText } from "@/lib/shareLines";
import type { ExpenseRuleRow } from "@/lib/types";

export function Regeln() {
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: ["rules"], queryFn: () => get<{ rules: ExpenseRuleRow[]; defaultMarginPct: number }>("/api/expense-rules") });
  // All clients incl. former ones, so existing lines still show their client; the pickers hide former ones.
  const clients = useQuery(allClientsQuery);
  const [editing, setEditing] = useState<ExpenseRuleRow | null>(null);
  const remove = useMutation({
    mutationFn: (id: number) => del(`/api/expense-rules/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rules"] }); toast.success("Regel gelöscht"); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-5">
      <PageHeader title="Regeln" parent={{ to: "/ausgaben", label: "Ausgaben" }} />
      <p className="max-w-prose text-sm text-muted-foreground">Neue Ausgaben eines Empfängers werden beim Abgleich automatisch so aufgeteilt. Eine Zeile kann eine feste Rechnungs-Nr. oder „nur intern“ mitbringen, dann landet der Anteil nicht unter Verrechnung. Bereits zugeordnete Ausgaben bleiben beim Bearbeiten und Löschen unverändert.</p>
      <Table className="table-fixed">
        <colgroup><col style={{ width: "24%" }} /><col /><col className="max-sm:hidden" style={{ width: "20%" }} /><col className="max-sm:hidden" style={{ width: "7rem" }} /><col style={{ width: "5.5rem" }} /></colgroup>
        <TableHeader>
          <TableRow className="hover:bg-transparent [&>th]:text-muted-foreground">
            <TableHead>Empfänger</TableHead><TableHead>Aufteilung</TableHead><TableHead className="max-sm:hidden">Notiz</TableHead>
            <TableHead className="max-sm:hidden">Angelegt</TableHead><TableHead><span className="sr-only">Aktionen</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rules.isPending ? <SkeletonRows columns={5} rows={3} /> : (rules.data?.rules.length ?? 0) === 0 ? (
            <EmptyRow columns={5}>Keine Regeln. Beim Zuordnen einer Ausgabe „Regel merken“ anhaken.</EmptyRow>
          ) : rules.data?.rules.map((r) => (
            <TableRow key={r.id} className="h-12">
              <TableCell className="overflow-hidden font-medium"><span className="block truncate" title={r.payee_pattern}>{r.payee_pattern}</span></TableCell>
              <TableCell className="overflow-hidden"><span className="block truncate" title={ruleSharesText(r)}>{ruleSharesText(r)}</span></TableCell>
              <TableCell className="overflow-hidden text-muted-foreground max-sm:hidden"><span className="block truncate">{r.note}</span></TableCell>
              <TableCell className="tabular-nums text-muted-foreground max-sm:hidden">{date(r.created_at)}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon-sm" aria-label={`Regel für ${r.payee_pattern} bearbeiten`} title="Regel bearbeiten"
                  className="text-muted-foreground" onClick={() => setEditing(r)}>
                  <Pencil />
                </Button>
                <Button variant="ghost" size="icon-sm" aria-label={`Regel für ${r.payee_pattern} löschen`} title="Regel löschen"
                  className="text-muted-foreground hover:text-destructive" disabled={remove.isPending}
                  onClick={() => confirm(`Regel für „${r.payee_pattern}“ löschen?`) && remove.mutate(r.id)}>
                  <Trash2 />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <RuleDialog rule={editing} clients={clients.data?.clients ?? []} defaultMarginPct={rules.data?.defaultMarginPct ?? 25} onClose={() => setEditing(null)} />
    </div>
  );
}
